import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import { addToCart, setOpcionesProducto } from '../../actions/cartActions';
import LoaderProducto from './LoaderProducto';
import {
  obtenerProductoTitulo,
  cargarCaracteristicaProducto,
  cargarAccesoriosProducto,
} from '../../utils/loaders';
import SEO from '../SEO';

function InformacionProducto() {
  const { titulo } = useParams();
  const dispatch = useDispatch();
  const [producto, setProducto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { handleSubmit, control, setValue } = useForm();
  const [productoCantidad, setProductoCantidad] = useState(1);
  const [caracteristicas, setCaracteristicas] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  const [id, setId] = useState(null);

  const seoData = useMemo(() => {
    if (!producto) {
      return {
        title: 'Producto',
        description:
          'Ficha de producto de radiocomunicación en ATST: radios, accesorios y soluciones profesionales en Chile.',
        canonicalPath: `/producto/${encodeURIComponent(titulo)}`,
        image: undefined,
      };
    }

    const plainDescription = producto.producto_descripcion
      ? producto.producto_descripcion.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      : '';

    const description = plainDescription
      ? `${plainDescription.substring(0, 155)}${plainDescription.length > 155 ? '…' : ''}`
      : 'Ficha técnica y detalles del producto de radiocomunicación en ATST.';

    return {
      title: producto.producto_titulo,
      description,
      canonicalPath: `/producto/${encodeURIComponent(producto.producto_titulo)}`,
      image: producto.producto_imagen_url,
    };
  }, [producto, titulo]);

  const increment = () => {
    const newCount = productoCantidad + 1;
    setProductoCantidad(newCount);
    setValue('productoCantidad', newCount);
  };

  const decrement = () => {
    const newCount = Math.max(1, productoCantidad - 1);
    setProductoCantidad(newCount);
    setValue('productoCantidad', newCount);
  };

  const miToastPersonalizado = (productoData) =>
    toast.custom((t) => {
      const customData = productoData || {};
      return (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start gap-3">
              <img
                className="h-12 w-12 rounded-lg object-contain bg-gray-50 border border-gray-100"
                src={customData.producto_imagen_url}
                alt={customData.producto_titulo}
                onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }}
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0f274b]">
                  {customData.producto_titulo}
                </p>
                <p className="text-xs text-[#00BEE7] font-semibold mt-0.5">Añadido a tu cotización ✓</p>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2 select-none">
                  {customData.producto_descripcion
                    ? customData.producto_descripcion.substring(0, 85) +
                      (customData.producto_descripcion.length > 50 ? '...' : '')
                    : ''}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-100">
            <Link
              to="/cotizaciones"
              className="flex items-center justify-center px-4 text-xs font-bold text-[#00BEE7] hover:text-[#007fa0] transition-colors"
            >
              Ver
            </Link>
          </div>
        </div>
      );
    });

  const onSubmit = (data) => {
    const requiereSeleccion =
      caracteristicas.length > 0 &&
      (producto?.subcategoria_nombre === 'Radios Portátiles' ||
        producto?.subcategoria_nombre === 'Radios Móviles') &&
      (caracteristicas[0].es_digital === 'SI' || caracteristicas[0].es_analoga === 'SI');

    if (requiereSeleccion && (!data.tipo || !data.banda)) {
      toast.error('Por favor, selecciona un tipo y una banda para el producto.');
      return;
    }

    miToastPersonalizado(producto);
    dispatch(addToCart(producto, productoCantidad));
    if (data.banda || data.tipo) {
      const tipoNormalizado = data.tipo === 'Analogo' ? 'Analógico' : data.tipo;
      dispatch(setOpcionesProducto(producto.producto_id, data.banda, tipoNormalizado));
    }
  };

  useEffect(() => {
    const cargarProducto = async () => {
      setIsLoading(true);
      const { data, error: fetchError } = await obtenerProductoTitulo(titulo);
      if (fetchError) {
        setIsLoading(false);
        setError(fetchError);
        return;
      }
      if (data) {
        setProducto(data);
        setId(data.producto_id);
      }
      setIsLoading(false);
    };
    cargarProducto();
  }, [titulo]);

  useEffect(() => {
    if (producto) {
      const cargarAccesorios = async () => {
        setIsLoading(true);
        const { data, error: fetchError } = await cargarAccesoriosProducto(id);
        if (fetchError) {
          setIsLoading(false);
          setError(fetchError);
          return;
        }
        if (data) setAccesorios(data);
        setIsLoading(false);
      };
      cargarAccesorios();
    }
  }, [producto, id]);

  useEffect(() => {
    if (producto) {
      const cargarCaracteristicas = async () => {
        setIsLoading(true);
        // eslint-disable-next-line prettier/prettier
        const { data, error: fetchError } = await cargarCaracteristicaProducto(id);
        if (fetchError) {
          setIsLoading(false);
          setError(fetchError);
          return;
        }
        if (data) setCaracteristicas(data);
        setIsLoading(false);
      };
      cargarCaracteristicas();
    }
  }, [producto, id]);

  const showBandaSelector =
    caracteristicas.length > 0 &&
    (producto?.subcategoria_nombre === 'Radios Portátiles' ||
      producto?.subcategoria_nombre === 'Radios Móviles') &&
    caracteristicas[0].tiene_vhf === 'SI';

  const showTipoSelector =
    caracteristicas.length > 0 &&
    (producto?.subcategoria_nombre === 'Radios Portátiles' ||
      producto?.subcategoria_nombre === 'Radios Móviles') &&
    (caracteristicas[0].es_digital === 'SI' || caracteristicas[0].es_analoga === 'SI');

  return (
    <main className="bg-gray-50 min-h-screen">
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonicalPath={seoData.canonicalPath}
        image={seoData.image}
        type="product"
      />
      <Toaster position="bottom-right" />

      {error && (
        <div className="w-full flex justify-center items-center py-20">
          <p className="text-xl font-bold text-red-500">Error al cargar el producto</p>
        </div>
      )}

      {isLoading && (
        <div className="w-full flex justify-center items-center py-20">
          <LoaderProducto />
        </div>
      )}

      {!isLoading && !error && producto && (
        <>
          {/* Breadcrumb */}
          <div className="bg-white border-b border-gray-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
                <Link to="/" className="text-gray-400 hover:text-[#007fa0] font-medium transition-colors">Inicio</Link>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <Link to="/productos" className="text-gray-400 hover:text-[#007fa0] font-medium transition-colors">Productos</Link>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-[#0f274b] font-semibold truncate max-w-xs">{producto.producto_titulo}</span>
              </nav>
            </div>
          </div>

          {/* Main product section */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">

              {/* Left: Product image */}
              <div className="md:sticky md:top-24 self-start">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex items-center justify-center min-h-[320px] md:min-h-[420px] group overflow-hidden">
                  <img
                    src={producto.producto_imagen_url}
                    className="w-full max-h-96 object-contain transition-transform duration-500 group-hover:scale-105"
                    alt={producto.producto_titulo}
                    onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }}
                  />
                </div>
                {/* Category + subcategory badges */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {producto.categoria_nombre && (
                    <span className="bg-[#0f274b] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      {producto.categoria_nombre}
                    </span>
                  )}
                  {producto.subcategoria_nombre && (
                    <span className="bg-[#00BEE7]/15 text-[#007fa0] border border-[#00BEE7]/30 text-xs font-bold px-3 py-1.5 rounded-full">
                      {producto.subcategoria_nombre}
                    </span>
                  )}
                  {caracteristicas.length > 0 && caracteristicas[0].tiene_vhf === 'SI' && (
                    <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1.5 rounded-full">VHF</span>
                  )}
                  {caracteristicas.length > 0 && caracteristicas[0].tiene_uhf === 'SI' && (
                    <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1.5 rounded-full">UHF</span>
                  )}
                  {caracteristicas.length > 0 && caracteristicas[0].es_digital === 'SI' && (
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold px-3 py-1.5 rounded-full">Digital</span>
                  )}
                  {caracteristicas.length > 0 && caracteristicas[0].es_analoga === 'SI' && (
                    <span className="bg-purple-50 text-purple-700 border border-purple-200 text-xs font-bold px-3 py-1.5 rounded-full">Analógico</span>
                  )}
                </div>
              </div>

              {/* Right: Product info */}
              <div className="flex flex-col">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#0f274b] leading-tight">
                  {producto.producto_titulo}
                </h1>

                <div className="h-1 w-16 bg-[#00BEE7] rounded-full mt-4 mb-5" />

                {/* Description */}
                <div
                  id="productoDescripcion"
                  className="text-sm text-gray-600 leading-relaxed max-h-40 overflow-y-auto scroll-container prose prose-sm"
                >
                  {/* eslint-disable react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: producto.producto_descripcion }} />
                  {/* eslint-enable react/no-danger */}
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">

                  {/* Banda selector */}
                  {showBandaSelector && (
                    <div>
                      <p className="text-xs font-extrabold text-gray-500 uppercase tracking-widest mb-2.5">Banda de frecuencia</p>
                      <Controller
                        name="banda"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div className="flex gap-2 flex-wrap">
                            {caracteristicas[0].tiene_vhf === 'SI' && (
                              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                              <label className={`cursor-pointer px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-150 ${field.value === 'VHF' ? 'border-[#00BEE7] bg-[#0f274b] text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-[#00BEE7]/50'}`}>
                                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                                <input {...field} type="radio" value="VHF" className="hidden" />
                                VHF
                              </label>
                            )}
                            {caracteristicas[0].tiene_uhf === 'SI' && (
                              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                              <label className={`cursor-pointer px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-150 ${field.value === 'UHF' ? 'border-[#00BEE7] bg-[#0f274b] text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-[#00BEE7]/50'}`}>
                                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                                <input {...field} type="radio" value="UHF" className="hidden" />
                                UHF
                              </label>
                            )}
                          </div>
                        )}
                      />
                    </div>
                  )}

                  {/* Tipo selector */}
                  {showTipoSelector && (
                    <div>
                      <p className="text-xs font-extrabold text-gray-500 uppercase tracking-widest mb-2.5">Tipo de tecnología</p>
                      <Controller
                        name="tipo"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div className="flex gap-2 flex-wrap">
                            {caracteristicas[0].es_digital === 'SI' && (
                              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                              <label className={`cursor-pointer px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-150 ${field.value === 'Digital' ? 'border-[#00BEE7] bg-[#0f274b] text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-[#00BEE7]/50'}`}>
                                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                                <input {...field} type="radio" value="Digital" className="hidden" />
                                Digital
                              </label>
                            )}
                            {caracteristicas[0].es_analoga === 'SI' && (
                              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                              <label className={`cursor-pointer px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-150 ${field.value === 'Analógico' ? 'border-[#00BEE7] bg-[#0f274b] text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-[#00BEE7]/50'}`}>
                                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                                <input {...field} type="radio" value="Analógico" className="hidden" />
                                Analógico
                              </label>
                            )}
                          </div>
                        )}
                      />
                    </div>
                  )}

                  {/* Quantity + CTA */}
                  <div className="pt-2 space-y-3">
                    <div>
                      <p className="text-xs font-extrabold text-gray-500 uppercase tracking-widest mb-2.5">Cantidad</p>
                      <Controller
                        name="productoCantidad"
                        control={control}
                        defaultValue={productoCantidad}
                        render={({ field }) => (
                          <div className="inline-flex items-center border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
                            <button
                              type="button"
                              onClick={decrement}
                              className="w-10 h-10 flex items-center justify-center text-[#0f274b] hover:bg-gray-50 font-bold text-lg transition-colors"
                            >
                              −
                            </button>
                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <input {...field} type="number" min="1" className="w-12 h-10 text-center font-bold text-[#0f274b] border-x-2 border-gray-200 outline-none focus:ring-0 bg-white text-sm" readOnly />
                            <button
                              type="button"
                              onClick={increment}
                              className="w-10 h-10 flex items-center justify-center text-[#0f274b] hover:bg-gray-50 font-bold text-lg transition-colors"
                            >
                              +
                            </button>
                          </div>
                        )}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-[#0f274b] hover:bg-[#00BEE7] text-white hover:text-[#0f274b] font-extrabold py-3.5 px-6 rounded-xl text-base transition-all duration-200 shadow-lg shadow-[#0f274b]/20 hover:shadow-[#00BEE7]/20 flex items-center justify-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Agregar a cotización
                    </button>

                    {producto.subcategoria_nombre !== 'Accesorios' && producto.producto_pdf_url && (
                      <a
                        href={producto.producto_pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full border-2 border-[#00BEE7]/40 hover:border-[#00BEE7] text-[#007fa0] hover:text-[#0f274b] hover:bg-[#00BEE7]/10 font-bold py-3 px-6 rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Descargar ficha técnica (PDF)
                      </a>
                    )}
                  </div>
                </form>

                {/* Trust signals */}
                <div className="mt-6 pt-5 border-t border-gray-100 grid grid-cols-3 gap-3">
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-9 h-9 rounded-xl bg-[#00BEE7]/10 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 font-semibold leading-tight">Distribuidor autorizado</p>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-9 h-9 rounded-xl bg-[#00BEE7]/10 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 font-semibold leading-tight">Soporte técnico</p>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-9 h-9 rounded-xl bg-[#00BEE7]/10 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 font-semibold leading-tight">+20 años de experiencia</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accessories section */}
          {producto.subcategoria_nombre !== 'Accesorios' && accesorios.length > 0 && (
            <div className="border-t border-gray-200 bg-white">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                <div className="mb-6">
                  <span className="inline-block bg-[#00BEE7]/10 border border-[#00BEE7]/30 text-[#00BEE7] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-2">
                    Compatibles
                  </span>
                  <h2 className="text-xl font-extrabold text-[#0f274b]">
                    Accesorios para el {producto.producto_titulo}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    Potencia tu equipo con accesorios diseñados específicamente para este modelo.
                  </p>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-4 scroll-container">
                  {accesorios.map((accesorio) => (
                    <Link
                      to={{
                        pathname: `/producto/${accesorio.informacion_accesorio.titulo}`,
                        state: { id: accesorio.informacion_accesorio.id },
                      }}
                      onClick={() =>
                        localStorage.setItem('productoId', accesorio.informacion_accesorio.id)
                      }
                      className="group flex-none w-36 md:w-44 flex flex-col rounded-xl border border-gray-100 hover:border-[#00BEE7]/60 hover:shadow-md transition-all duration-200 overflow-hidden bg-white"
                      key={accesorio.id}
                    >
                      <div className="h-28 md:h-32 overflow-hidden bg-gray-50 flex items-center justify-center p-3">
                        <img
                          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                          src={accesorio.informacion_accesorio.imagen_url}
                          alt={accesorio.informacion_accesorio.titulo}
                          onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }}
                        />
                      </div>
                      <div className="p-2.5 bg-[#0f274b] group-hover:bg-[#00BEE7] transition-colors duration-300">
                        <p
                          title={accesorio.informacion_accesorio.titulo}
                          className="text-white group-hover:text-[#0f274b] font-bold text-xs text-center truncate transition-colors duration-300"
                        >
                          {accesorio.informacion_accesorio.titulo}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}

export default InformacionProducto;
